import { useMemo, useState } from 'react';

import {
  Box,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { PageHeader } from '@karehero/llama/pwa/atoms/PageHeader/PageHeader';

import StatusBar from '@/designs-dev/device/StatusBar';
import Navigation from '@/karehero-app/shell/Navigation';
import ActionDetail from '@/karehero-app/actions/ActionDetail';
import { type Item } from '@/components/wrapped/ActionCard';
import Card from '@/components/wrapped/Card';
import CallSummary from '@/karehero-app/care/CallSummary';
import DocRow from '@/karehero-app/care/DocRow';
import FlowPlaceholder from '@/karehero-app/care/FlowPlaceholder';
import PushScreen from '@/designs-dev/device/PushScreen';
import ShortlistHome from '@/karehero-app/care/shortlists/ShortlistHome';
import ShortlistView from '@/karehero-app/care/shortlists/ShortlistView';
import ProviderDetail from '@/karehero-app/care/shortlists/ProviderDetail';
import {
  SHORTLISTS,
  type ProviderStatus,
  type ShortlistProvider,
} from '@/karehero-app/care/shortlists/fixtures';
import { DocActionsSheet, EditDocSheet } from '@/karehero-app/care/DocSheets';
import {
  CALLS,
  DOCS,
  FORMS,
  TOOLS,
  type CallItem,
  type DocItem,
} from '@/karehero-app/care/fixtures';
import ScreenRoot from '@/components/wrapped/ScreenRoot';

const SCREEN_PX = 2; // 16px gutter (matches the Figma 343px content width)

// Frosted (translucent + blur) background for the sticky footer CTA.
const frostedFooter = {
  backgroundColor: (theme: any) =>
    alpha(theme.palette.background.default, 0.72),
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
};

export type ProfileTab = 'calls' | 'docs' | 'forms' | 'tools';

const PROFILE_SEGMENTS: { id: ProfileTab; label: string }[] = [
  { id: 'calls', label: 'Calls' },
  { id: 'docs', label: 'Docs' },
  { id: 'forms', label: 'Forms' },
  { id: 'tools', label: 'Tools' },
];

const CALL_SECTIONS: { id: CallItem['status']; label: string }[] = [
  { id: 'upcoming', label: 'UPCOMING' },
  { id: 'past', label: 'PAST' },
];

// The Profile tab: the record of the person being cared for, split across
// Calls / Docs / Forms / Tools segments. Calls drill into call summaries
// (and from there into linked actions); docs have a kebab action sheet and
// an edit drawer; forms and tools lead to placeholder flows for now.
//
// This was the Care tab. Arranging care is its own tab now (shortlists), so what
// is left here is the record rather than the doing, and it is named for that.
const ProfileScreen = ({
  initialTab = 'calls',
  emptyCalls = false,
  emptyDocs = false,
  onNotifications,
}: {
  initialTab?: ProfileTab;
  emptyCalls?: boolean;
  emptyDocs?: boolean;
  /** Bell tap — opens the notification centre. Omitted in stories/handover. */
  onNotifications?: () => void;
}) => {
  const [tab, setTab] = useState<ProfileTab>(initialTab);
  const [calls, setCalls] = useState<CallItem[]>(emptyCalls ? [] : CALLS);
  const [docs, setDocs] = useState<DocItem[]>(emptyDocs ? [] : DOCS);

  const [openCallId, setOpenCallId] = useState<number | null>(null);
  const [actionDetail, setActionDetail] = useState<Item | null>(null);
  const [docMenu, setDocMenu] = useState<DocItem | null>(null);
  const [editingDoc, setEditingDoc] = useState<DocItem | null>(null);
  const [flow, setFlow] = useState<string | null>(null);

  // Shortlist viewing (Tools → Care shortlist): home → list → provider, a
  // stack of pushed pages. Tracked by id so the pushed views reflect live
  // status changes (securing a home) while they're open.
  //
  // The Care tab is the way in now. This route stays because the shortlists v2
  // flows page documents it, and because it is what production ships today.
  const [shortlistsOpen, setShortlistsOpen] = useState(false);
  const [openShortlistId, setOpenShortlistId] = useState<string | null>(null);
  const [openProviderId, setOpenProviderId] = useState<string | null>(null);
  // Per-home secured status the family sets, keyed by provider id (shared
  // across every shortlist the home appears in). prevStatus remembers the
  // status before securing so un-securing can revert to it.
  const [statusOverrides, setStatusOverrides] = useState<
    Record<string, ProviderStatus>
  >({});
  const [prevStatus, setPrevStatus] = useState<Record<string, ProviderStatus>>(
    {},
  );

  // Apply the family's secured overrides on top of the CAM's published order.
  const resolvedShortlists = useMemo(
    () =>
      SHORTLISTS.map((sl) => ({
        ...sl,
        providers: sl.providers.map((p) => ({
          ...p,
          status: statusOverrides[p.id] ?? p.status,
        })),
      })),
    [statusOverrides],
  );

  const openShortlist =
    resolvedShortlists.find((s) => s.id === openShortlistId) ?? null;
  const openProvider =
    resolvedShortlists
      .flatMap((s) => s.providers)
      .find((p) => p.id === openProviderId) ?? null;

  // Family toggles a home's secured status. provider is already resolved, so
  // provider.status is the current effective status. Un-securing reverts to
  // the remembered previous status, or Contacted if none was recorded.
  const toggleSecured = (provider: ShortlistProvider) => {
    const { id } = provider;
    if (provider.status === 'Secured') {
      const revert = prevStatus[id] ?? 'Contacted';
      setStatusOverrides((o) => ({ ...o, [id]: revert }));
    } else {
      setPrevStatus((p) => ({ ...p, [id]: provider.status }));
      setStatusOverrides((o) => ({ ...o, [id]: 'Secured' }));
    }
  };

  const openCall = calls.find((c) => c.id === openCallId) ?? null;

  // Toggle a linked action's done state (from the summary list or the sheet).
  const setActionDone = (id: number, done: boolean) =>
    setCalls((prev) =>
      prev.map((c) => ({
        ...c,
        linkedActions: c.linkedActions.map((a) =>
          a.id === id ? { ...a, done } : a,
        ),
      })),
    );
  const toggleAction = (id: number) => {
    const current = openCall?.linkedActions.find((a) => a.id === id);
    setActionDone(id, !current?.done);
  };

  // Simulated upload: the new row spins for a moment, then settles.
  const uploadDoc = () => {
    const id = Date.now();
    setDocs((prev) => [
      { id, name: 'example_file.pdf', uploading: true },
      ...prev,
    ]);
    setTimeout(() => {
      setDocs((prev) =>
        prev.map((d) => (d.id === id ? { ...d, uploading: false } : d)),
      );
    }, 1800);
  };

  const deleteDoc = (id: number) =>
    setDocs((prev) => prev.filter((d) => d.id !== id));
  const renameDoc = (id: number, name: string) =>
    setDocs((prev) => prev.map((d) => (d.id === id ? { ...d, name } : d)));

  // ── Main tab body ──────────────────────────────────────────────────────────

  const upcoming = calls.filter((c) => c.status === 'upcoming');
  const past = calls.filter((c) => c.status === 'past');

  const cta =
    tab === 'calls' && calls.length > 0
      ? { label: 'Book a call' }
      : (tab === 'docs' && docs.length > 0) || tab === 'forms'
        ? {
            label: 'Upload document',
            onPress: tab === 'docs' ? uploadDoc : undefined,
          }
        : null;

  const emptyState = (title: string, button: string, onPress?: () => void) => (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: SCREEN_PX,
      }}
    >
      <Box sx={{ mb: 3 }}>
        <PageHeader title={title} isCentered />
      </Box>
      <Box sx={{ width: '100%' }}>
        <Button
          variant='primary'
          size='lg'
          isFullWidth
          ariaLabel={button}
          onPress={onPress}
        >
          {button}
        </Button>
      </Box>
    </Box>
  );

  return (
    <ScreenRoot relative>
      <StatusBar />

      <Navigation onNotifications={onNotifications} avatarInitials='OP' />

      <Box sx={{ px: SCREEN_PX, pt: 2, flexShrink: 0 }}>
        <ToggleButtonGroup
          className='segmented'
          exclusive
          fullWidth
          value={tab}
          onChange={(_, v) => v != null && setTab(v)}
        >
          {PROFILE_SEGMENTS.map((s) => (
            <ToggleButton key={s.id} value={s.id}>
              {s.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {tab === 'calls' && calls.length === 0 ? (
        emptyState('No calls yet', 'Book a call')
      ) : tab === 'docs' && docs.length === 0 ? (
        emptyState('No docs yet', 'Upload document', uploadDoc)
      ) : (
        <Box
          sx={{ flex: 1, overflow: 'auto', px: SCREEN_PX, pt: 2, pb: '96px' }}
        >
          {tab === 'calls' && (
            <Stack spacing={1}>
              {CALL_SECTIONS.map((section) => {
                const items = (
                  section.id === 'upcoming' ? upcoming : past
                ).filter(Boolean);
                if (items.length === 0) return null;
                return (
                  <Box key={section.id}>
                    <Typography
                      variant='overline'
                      color='text.secondary'
                      sx={{ display: 'block', mt: 1, mb: 1 }}
                    >
                      {section.label}
                    </Typography>
                    <Stack spacing={1.5}>
                      {items.map((c) => (
                        <Card
                          key={c.id}
                          variant='compact'
                          title={c.title}
                          subtitle={[
                            `${c.when} · with ${c.withName}`,
                            `From ${c.from}`,
                          ]}
                          onClick={() => setOpenCallId(c.id)}
                        />
                      ))}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          )}

          {tab === 'docs' && (
            <Stack spacing={1.5}>
              {docs.map((d) => (
                <DocRow key={d.id} doc={d} onMenu={setDocMenu} />
              ))}
            </Stack>
          )}

          {tab === 'forms' && (
            <Stack spacing={2}>
              {FORMS.map((f) => (
                <Card
                  key={f.id}
                  title={f.title}
                  titleVariant='h3'
                  subtitle={`${f.answered}/${f.total} questions answered`}
                  onClick={() => setFlow('Current form flow')}
                />
              ))}
            </Stack>
          )}

          {tab === 'tools' && (
            <Stack spacing={2}>
              {TOOLS.map((t) => (
                <Card
                  key={t.id}
                  title={t.title}
                  titleVariant='h3'
                  subtitle={t.description}
                  onClick={() =>
                    t.id === 'care-shortlist'
                      ? setShortlistsOpen(true)
                      : setFlow('Current calculator flow')
                  }
                />
              ))}
            </Stack>
          )}
        </Box>
      )}

      {cta && (
        <Box
          sx={[
            {
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              px: SCREEN_PX,
              py: 1.5,
            },
            frostedFooter,
          ]}
        >
          <Button
            variant='primary'
            size='lg'
            isFullWidth
            ariaLabel={cta.label}
            onPress={cta.onPress}
          >
            {cta.label}
          </Button>
        </Box>
      )}

      {/* Pushed stack pages: slide in from the right over the tab. */}
      {openCall && (
        <PushScreen onClose={() => setOpenCallId(null)}>
          {(close) => (
            <CallSummary
              call={openCall}
              onBack={close}
              onToggleAction={toggleAction}
              onOpenAction={setActionDetail}
            />
          )}
        </PushScreen>
      )}
      {flow && (
        <PushScreen onClose={() => setFlow(null)}>
          {(close) => <FlowPlaceholder title={flow} onBack={close} />}
        </PushScreen>
      )}

      {/* Shortlist viewing: home → shortlist → provider detail, each pushed
          over the last. */}
      {shortlistsOpen && (
        <PushScreen onClose={() => setShortlistsOpen(false)}>
          {(close) => (
            <ShortlistHome
              shortlists={resolvedShortlists}
              onBack={close}
              onOpen={(s) => setOpenShortlistId(s.id)}
            />
          )}
        </PushScreen>
      )}
      {openShortlist && (
        <PushScreen onClose={() => setOpenShortlistId(null)}>
          {(close) => (
            <ShortlistView
              shortlist={openShortlist}
              onBack={close}
              onOpenProvider={(p) => setOpenProviderId(p.id)}
              onToggleSecured={toggleSecured}
            />
          )}
        </PushScreen>
      )}
      {openProvider && (
        <ProviderDetail
          asSheet
          provider={openProvider}
          onBack={() => setOpenProviderId(null)}
          onToggleSecured={toggleSecured}
        />
      )}

      {actionDetail && (
        <ActionDetail
          action={actionDetail}
          onClose={() => setActionDetail(null)}
          onSetDone={setActionDone}
          resolveLinkedCall={(linked) =>
            calls.find((c) => c.when === linked.when) ?? null
          }
        />
      )}

      {docMenu && (
        <DocActionsSheet
          onView={() => setDocMenu(null)}
          onEdit={() => {
            setEditingDoc(docMenu);
            setDocMenu(null);
          }}
          onDelete={() => {
            deleteDoc(docMenu.id);
            setDocMenu(null);
          }}
          onClose={() => setDocMenu(null)}
        />
      )}

      {editingDoc && (
        <EditDocSheet
          doc={editingDoc}
          onSave={(name) => {
            renameDoc(editingDoc.id, name);
            setEditingDoc(null);
          }}
          onClose={() => setEditingDoc(null)}
        />
      )}
    </ScreenRoot>
  );
};

export default ProfileScreen;
